import React from 'react';
import MathJaxHTMLContent from '@common/MathJaxHTMLContent';
import CircularLoader from '@common/CircularLoader';
import { Tooltip } from 'antd';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const QuestionCard = ({
                          currentQuestion,
                          questionIndex,
                          questionList,
                          selectedQuestionId,
                          frontierIndex,
                          favorites,
                          notes,
                          checkedQuestions,
                          isCorrect,
                          isSubmitting,
                          isLoadingQuestion,
                          isCheckDisabled,
                          userAnswers,
                          questionContentRef,
                          onToggleFavorite,
                          onOpenNotes,
                          onCheckAnswer,
                          onRetry,
                          onSelectQuestion,
                          onInputChange,
                          onRestoreInputs,
                          onBackToList,
                          formatUserAnswer,
                          formatCorrectAnswer,
                      }) => {
    const currentIndex = questionList.findIndex(q => q.questionId === selectedQuestionId);

    const canGoPrevious = !isSubmitting && currentIndex > 0;
    const canGoNext = !isSubmitting && currentIndex < frontierIndex;

    return (
        <div className="flex1 flexColumn min-width-0">
            <div className="ch-card padding--m ch-surface-bg flex1 flexColumn">

                {/* Header: Question number + tool buttons */}
                <div className="flexRow flexSpaceBetween flexAlignCentre margin-b--s padding-b--s border-b--s">
                    <div>
                        <span className="ch-text-large ch-text-bold">
                            Question {questionIndex + 1}
                        </span>
                        <span className="ch-text-small text-muted margin-l--s">
                            ID: {currentQuestion.questionId}
                        </span>
                    </div>

                    <div className="flexRow smallGap">
                        <Tooltip title="Toggle Favorite">
                            <button
                                onClick={() => onToggleFavorite(currentQuestion.questionId)}
                                className={`ch-icon-btn ch-btn-star ${favorites.includes(currentQuestion.questionId) ? 'ch-btn-star-active' : ''}`}
                            >
                                {favorites.includes(currentQuestion.questionId) ? <StarIcon /> : <StarBorderIcon />}
                            </button>
                        </Tooltip>
                        <Tooltip title={notes[currentQuestion.questionId] ? 'View Notes' : 'Add Notes'}>
                            <button
                                onClick={() => onOpenNotes(currentQuestion.questionId)}
                                className={`ch-icon-btn ch-btn-notes ${notes[currentQuestion.questionId] ? 'ch-btn-notes-active' : ''}`}
                            >
                                {notes[currentQuestion.questionId] ? <DescriptionIcon /> : <NoteAddIcon />}
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Note Preview */}
                {notes[currentQuestion.questionId] && (
                    <div className="margin-b--s padding--s ch-light-bg border-radius-component border--s">
                        <strong className="ch-text-bold">Note:</strong>
                        <p className="margin-t--xs ch-note-preview-text">
                            {notes[currentQuestion.questionId].length > 100
                                ? notes[currentQuestion.questionId].substring(0, 100) + '...'
                                : notes[currentQuestion.questionId]}
                        </p>
                    </div>
                )}

                {/* Question Body */}
                <MathJaxHTMLContent
                    key={currentQuestion.questionId}
                    refCallBack={el => {
                        questionContentRef.current[currentQuestion.questionId] = el;
                        onRestoreInputs(currentQuestion.questionId, el);
                    }}
                    className="margin-b--m ch-text-std flex1 ch-q-gen"
                    html={currentQuestion.formattedInstructions || currentQuestion.instructions || 'No question content'}
                    onKeyUp={() => onInputChange(currentQuestion.questionId)}
                    onChange={() => onInputChange(currentQuestion.questionId)}
                />

                {/* Check / Retry */}
                <div className="flexRow flexWrap smallGap flexAlignCentre">
                    {!isSubmitting && !checkedQuestions[currentQuestion.questionId] && (
                        <button
                            onClick={() => onCheckAnswer(currentQuestion.questionId)}
                            disabled={isCheckDisabled || isLoadingQuestion}
                            className="ch-button ch-button-icon-l ch-btn-check"
                        >
                            <CheckIcon className="ch-icon-sm" /> Check Answer
                        </button>
                    )}

                    {!isSubmitting && checkedQuestions[currentQuestion.questionId] && (
                        <button
                            onClick={() => onRetry(currentQuestion.questionId)}
                            disabled={isLoadingQuestion}
                            className="ch-button ch-button-icon-l ch-btn-outline"
                        >
                            <RefreshIcon className="ch-icon-sm" /> Retry
                        </button>
                    )}
                    {(isLoadingQuestion || isSubmitting) && <CircularLoader displayInElement={true} pageCentered={false}/>}
                </div>

                {/* Feedback Block */}
                {checkedQuestions[currentQuestion.questionId] && (
                    <div className="margin-t--s flexColumn smallGap" aria-live="polite">
                        {/* Your Answer */}
                        <div className={`ch-feedback-user-ans ${isCorrect[currentQuestion.questionId] ? 'ch-feedback-user-ans-correct' : 'ch-feedback-user-ans-incorrect'}`}>
                            <div className="flexRow flexAlignCentre smallGap">
                                {isCorrect[currentQuestion.questionId]
                                    ? <CheckIcon className="ch-icon" />
                                    : <span className="ch-icon ch-text-bold ch-center-elem" style={{ fontSize: '16px', width: '20px' }}>✗</span>
                                }
                                <div>
                                    <strong style={{ color: '#333' }}>Your Answer:</strong>
                                    <span className="margin-l--s ch-text-bold" style={{ color: '#000' }}>
                                        {formatUserAnswer(userAnswers[currentQuestion.questionId], currentQuestion.questionAnswerType)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Correct Answer */}
                        <div className="ch-feedback-correct-ans">
                            <div className="flexRow flexAlignCentre smallGap">
                                <DescriptionIcon className="ch-icon" style={{ fontSize: '18px' }} />
                                <div>
                                    <strong style={{ color: '#333' }}>Correct Answer:</strong>
                                    <MathJaxHTMLContent
                                        ElementTag="span"
                                        className="margin-l--s ch-text-bold"
                                        html={formatCorrectAnswer(currentQuestion.questionId, currentQuestion.correctAnswer, currentQuestion.questionAnswerType)}
                                    />
                                    {currentQuestion.questionAnswerType === 'CHECKBOX' && (
                                        <span className="margin-l--s ch-text-small">(Multiple correct)</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Navigation Row */}
            <div className="flexRow flexAlignCentre smallGap margin-t--m">
                <button
                    onClick={() => canGoPrevious && onSelectQuestion(questionList[currentIndex - 1].questionId)}
                    disabled={!canGoPrevious}
                    className="ch-button ch-button-icon-l ch-btn-outline"
                >
                    <ArrowBackIcon className="ch-icon-sm" /> Previous
                </button>

                <button
                    onClick={() => canGoNext && onSelectQuestion(questionList[currentIndex + 1].questionId)}
                    disabled={!canGoNext}
                    className="ch-button ch-button-icon-r ch-btn-outline"
                >
                    Next <ArrowForwardIcon className="ch-icon-sm" />
                </button>

                <button
                    onClick={onBackToList}
                    disabled={isSubmitting}
                    className="ch-btn-text margin-l--auto"
                >
                    Back to Quiz List
                </button>
            </div>
        </div>
    );
};

export default QuestionCard;